"use client";
import { useState } from "react";
const faqData = [
	{
		category: "Products",
		items: [
			{
				question: "How do your products differ from normal photo paper prints?",
				answer: (
					<>
						<p>
							Many photo papers are produced using optical brightening agents (OBA) such as bleach or titanium
							dioxide. These chemicals break down over time and cause your photos to fade and yellow (among
							other things). Without getting technical, the composition of traditional photo paper also has
							color-depth limitations, as well.
						</p>
						<p>
							The fine art paper we use provides us with extraordinary color accuracy and detail. Our paper is
							acid-free, lignen-free, and contains no OBAs. Instead of resin and OBAs like in photo paper,
							this fine art paper is made from archival alpha-cellulose, a thicker and naturally occurring
							fiber derived from cotton or wood. Paired with the use of archival inks, your printed product
							can withstand the test of time. This is truly a museum-quality product.
						</p>
					</>
				),
			},
			{
				question: "Does your fine art paper conform to ISO 9706?",
				answer: (
					<>
						<p>
							Yes. Your fine art product will be printed on high-end Gicleè baryta paper with archival pigment
							inks. The paper itself is 315gsm, has a semi-gloss finish, is made from alpha-cellulose, and is
							acid- and lignin-free.
						</p>
						<p>
							All of these technical specifications conform to the international standard outlined by{" "}
							<a className="underline" href="https://www.iso.org/standard/17562.html">
								ISO 9706
							</a>
							, offering a museum-quality print with excellent age-resistance.
						</p>
					</>
				),
			},
			{
				question: "Can you provide more details for framed products?",
				answer: (
					<p>
						Images are printed using archival methods as described above. Following printing, we dry mount
						your print to acid-free backing board. Archival matboard is then added to secure the print safely
						for long-term storage. Your supported print will then be carefully positioned behind acrylic glass
						which provides UV-protection to your print. After securing it within your chosen frame, it is
						sealed with backing paper. As a final step, we fix mounting hardware to the chosen
						frame-orientation and then your product is shipped out and ready to hang right out of the box!
					</p>
				),
			},
			// Add more items here
		],
	},
	{
		category: "Ordering",
		items: [
			{
				question: "What payment methods are accepted?",
				answer: (
					<>
						<p>
							We accept payments from Visa, MasterCard, Discover, and American Express. Payments from wallets
							such as Apple Pay, Google Pay, and Microsoft Pay are also supported.
						</p>
						<p>
							If you are a customer from Europe or Asia not using one of the card providers above, we may not
							support your card provider at this time. However, payments from Carte Bancaires, JCB, and China
							Union Pay should be accepted.
						</p>
					</>
				),
			},
			{
				question: "Are payments transmitted securely?",
				answer: (
					<p>
						Your payment and personal information is encrypted during checkout with industry standard AES-256
						and processed through a PCI-Service Level 1 system. If you want to know more, please review our{" "}
						<a className="underline" href="/privacy">
							Privacy Policy
						</a>
						.
					</p>
				),
			},
			{
				question: "Where is my Order ID/Number?",
				answer: (
					<p>
						You will find your Order ID in the confirmation email we send after you place your order. This can
						be used on our{" "}
						<a className="underline" href="/tracking">
							Order Tracking page
						</a>{" "}
						to get updates about the progress of your order.
					</p>
				),
			},
			// Add more items here
		],
	},
	{
		category: "Shipping",
		items: [
			{
				question: "How long does it take to receive my package?",
				answer: (
					<>
						<p>
							<strong>Unframed Prints</strong> will arrive in approximately 1 full working week from the time
							of ordering [or sooner!]. Processing and manufacturing requires at least 2-3 days and then we
							provide you with free 2-day shipping.
						</p>
						<p>
							<strong>Framed Prints</strong> will arrive in approximately 5-14 days. Unfortunately, we
							can&apos;t provide exact estimates at this time.
						</p>
					</>
				),
			},
			{
				question: "How can I track my order?",
				answer: (
					<p>
						Once your order has been shipped, you will receive a confirmation email from us with your order
						ID. You can see what stage of the process your order is in by entering your order number into the{" "}
						<a className="underline" href="/tracking">
							Order Tracking page
						</a>
						.
					</p>
				),
			},
			{
				question: "Do you offer free shipping?",
				answer: <p>Yes! We currently offer free shipping to our US-based customers.</p>,
			},
			{
				question: "Do you ship internationally?",
				answer: (
					<p>
						At this time we currently do not ship outside of the USA. However, we are in the process of
						building this infrastructure. If you are an international customer, please reach out to us in the
						meantime and we will absolutely get our product in your hands. Note that at this time there will
						be additional fees -- something we plan to eliminate in the future.
					</p>
				),
			},
			// Add more items here
		],
	},
	{
		category: "Returns",
		items: [
			{
				question: "What is your return policy?",
				answer: (
					<p>
						All pieces are made-to-order and customized to your specifications. Additionally, all prints,
						including open editioned prints, are registered in our{" "}
						<a className="underline" href="/registry">
							Art Registry
						</a>
						. As a result, we are currently unable to accept returns. You can read our full outline, which
						includes extenuating circumstances, here:{" "}
						<a className="underline" href="/returns">
							Return Policy
						</a>
						.
					</p>
				),
			},
			{
				question: "What can I do if my art arrives damaged?",
				answer: (
					<p>
						In the rare event that your art arrives damaged, please reach out through the{" "}
						<a href="https://www.micrographer.com/contact" className="underline">
							Contact Page
						</a>{" "}
						and{" "}
						<a className="underline" href="/returns">
							review our statement about damage reviews
						</a>
						.
					</p>
				),
			},
			{
				question: "Who should I contact if I have any other questions?",
				answer: (
					<p>
						You can contact our orders support team through the{" "}
						<a href="https://www.micrographer.com/contact" className="underline">
							Contact Page
						</a>
						. Please provide the order number [found in your order confirmation email], if your question
						relates to a submitted order.
					</p>
				),
			},
			// Add more items here
		],
	},
];

const FAQ = () => {
	// Composite key so opening a question in one category doesn't open the same index in another.
	const [openKey, setOpenKey] = useState<string | null>(null);

	return (
		<section className="mx-auto max-w-gallery px-5 py-section-sm sm:px-8">
			<header className="mx-auto max-w-prose text-center">
				<p className="label-caps mb-4 text-ink-muted">Answers</p>
				<h1 className="font-serif text-display font-light text-mg-black">Frequently asked</h1>
			</header>

			<div className="mx-auto mt-16 max-w-3xl [&_a:hover]:decoration-mg-gold-deep [&_a]:underline [&_a]:decoration-mg-gold-deep/50 [&_a]:underline-offset-4 [&_p:first-child]:mt-0 [&_p]:mt-3">
				{faqData.map((category, categoryIndex) => (
					<div key={categoryIndex} className="mb-16">
						<h2 className="label-caps mb-6 text-mg-gold-deep">{category.category}</h2>
						<div className="border-t border-mg-black/10">
							{category.items.map((item, index) => {
								const key = `${categoryIndex}-${index}`;
								const isOpen = openKey === key;
								return (
									<div key={key} className="border-b border-mg-black/10">
										<button
											type="button"
											onClick={() => setOpenKey(isOpen ? null : key)}
											aria-expanded={isOpen}
											className="flex w-full items-center justify-between gap-4 py-5 text-left"
										>
											<h3 className="font-serif text-lg font-medium text-mg-black">{item.question}</h3>
											<span
												className={`shrink-0 text-mg-gold-deep transition-transform duration-300 ease-gallery ${
													isOpen ? "rotate-45" : ""
												}`}
												aria-hidden
											>
												+
											</span>
										</button>
										<div className={isOpen ? "block pb-6" : "hidden"}>
											<div className="max-w-prose text-sm leading-relaxed text-ink-muted">{item.answer}</div>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				))}
			</div>
		</section>
	);
};

export default FAQ;
